import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPATIBLE_BRANDS, BRAND_CATEGORIES, type CompatibleBrand } from "@/data/compatibleBrands";
import { Search, Filter, Star, CheckCircle, ArrowUpRight } from "lucide-react";

interface BrandExplorerProps {
  className?: string;
}

export const BrandExplorer: React.FC<BrandExplorerProps> = ({ className = "" }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedIntegration, setSelectedIntegration] = useState<string>("all");
  const [showPopularOnly, setShowPopularOnly] = useState(false);

  const filteredBrands = useMemo(() => {
    let filtered = COMPATIBLE_BRANDS;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(brand => 
        brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        brand.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        brand.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(brand => brand.category === selectedCategory);
    }

    // Filter by integration level
    if (selectedIntegration !== "all") {
      filtered = filtered.filter(brand => brand.integrationLevel === selectedIntegration);
    }

    // Filter by popularity
    if (showPopularOnly) {
      filtered = filtered.filter(brand => brand.popularity === "High");
    }

    return filtered;
  }, [searchQuery, selectedCategory, selectedIntegration, showPopularOnly]);

  const getIntegrationColor = (level: CompatibleBrand['integrationLevel']) => {
    switch (level) {
      case 'Direct': return 'bg-green-100 text-green-800 border-green-300';
      case 'Gateway': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Matter': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Partner': return 'bg-orange-100 text-orange-800 border-orange-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getCategoryStats = () => {
    const stats = BRAND_CATEGORIES.map(category => ({
      category,
      count: COMPATIBLE_BRANDS.filter(brand => brand.category === category).length,
      popular: COMPATIBLE_BRANDS.filter(brand => brand.category === category && brand.popularity === 'High').length
    }));
    return stats;
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Search and Filter Controls */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek merken, beschrijvingen, categorieën..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Alle categorieën" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle categorieën</SelectItem>
              {BRAND_CATEGORIES.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedIntegration} onValueChange={setSelectedIntegration}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Integratie type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle integraties</SelectItem>
              <SelectItem value="Direct">Direct</SelectItem>
              <SelectItem value="Gateway">Via Gateway</SelectItem>
              <SelectItem value="Matter">Matter Support</SelectItem>
              <SelectItem value="Partner">Partner API</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center justify-between">
          <Button
            variant={showPopularOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPopularOnly(!showPopularOnly)}
            className="flex items-center gap-2"
          >
            <Star className={`h-4 w-4 ${showPopularOnly ? 'fill-current' : ''}`} />
            Populair ({COMPATIBLE_BRANDS.filter(b => b.popularity === 'High').length})
          </Button>
          <p className="text-sm text-muted-foreground">
            {filteredBrands.length} van {COMPATIBLE_BRANDS.length} merken
          </p>
        </div>
      </div>

      {/* Category Statistics */}
      {selectedCategory === "all" && !searchQuery && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {getCategoryStats().map(stat => (
            <Card key={stat.category} className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedCategory(stat.category)}>
              <div className="text-center">
                <h4 className="font-semibold text-sm mb-1">{stat.category}</h4>
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <span>{stat.count} merken</span>
                  {stat.popular > 0 && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-current text-yellow-500" />
                        <span>{stat.popular}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Brand Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredBrands.map((brand, index) => (
          <Card key={`${brand.name}-${index}`} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-primary/10 bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base group-hover:text-primary transition-colors flex items-center gap-2">
                    {brand.name}
                    {brand.popularity === 'High' && (
                      <Star className="h-4 w-4 fill-current text-yellow-500" />
                    )}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs mt-2">{brand.category}</Badge>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100" />
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{brand.description}</p>
              <div className="flex items-center justify-between">
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getIntegrationColor(brand.integrationLevel)}`}>
                  <CheckCircle className="h-3 w-3" />
                  {brand.integrationLevel}
                </div>
                <Badge variant={brand.popularity === 'High' ? 'default' : 'secondary'} className="text-xs">
                  {brand.popularity === 'High' ? 'Populair' : brand.popularity === 'Medium' ? 'Gemiddeld' : 'Niche'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredBrands.length === 0 && (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Geen merken gevonden voor de huidige filters.</p>
            <Button variant="outline" onClick={() => {
              setSearchQuery("");
              setSelectedCategory("all");
              setSelectedIntegration("all");
              setShowPopularOnly(false);
            }} className="mt-4">
              Reset filters
            </Button>
          </div>
        </div>
      )}

      {/* Integration Guide CTA */}
      <Card className="bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">
            Hulp nodig bij integratie?
          </h3>
          <p className="text-muted-foreground mb-4">
            Onze Loxone specialisten helpen u bij het selecteren en integreren van de juiste merken voor uw smart home.
          </p>
          <Button onClick={() => {
            (window as any).Calendly?.initPopupWidget?.({
              url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
            });
          }}>
            Plan Gratis Adviesgesprek
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandExplorer;